import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/site/Layout";
import { Picture, variantsFor } from "@/components/site/Picture";
import { Section, SectionEyebrow } from "@/components/site/Section";
import { CTAButton } from "@/components/site/Button";

import { StatsBand } from "@/components/site/StatsBand";
import { ValuesSection } from "@/components/site/ValuesSection";
import { CTABanner } from "@/components/site/CTABanner";
import { ReviewsWidget } from "@/components/site/ReviewsWidget";
import { BRAND } from "@/lib/brand";
import heroAz from "@/assets/hero-az.jpg";
import roundtableImg from "@/assets/roundtable.jpg";
import agentsAz from "@/assets/agents-az.jpg";
import { LazyUSMarketsMap as USMarketsMap } from "@/components/site/LazyUSMarketsMap";
import { locations } from "@/lib/locations";
import { MARKETS_COUNT, STATS_TEXT } from "@/lib/stats";
import { ArrowRight } from "lucide-react";

export const Route = createFileRoute("/")({
  head: () => ({
    links: [
      {
        rel: "preload",
        as: "image",
        href: variantsFor(heroAz).avif ?? heroAz,
        type: variantsFor(heroAz).avif ? "image/avif" : undefined,
        fetchpriority: "high",
      },
      { rel: "canonical", href: "https://joinkcn.com/" },
    ],
    meta: [
      { title: "Kristan Cole Network | Join the Top Keller Williams Realty Team" },
      {
        name: "description",
        content:
          "Join the top Keller Williams real estate team. 40+ years of leadership, $1B+ closed in 2025, and the COLE Method that takes agents from producer to CEO.",
      },
      {
        property: "og:title",
        content: "Kristan Cole Network | Join the Top Keller Williams Realty Team",
      },
      {
        property: "og:description",
        content:
          "Join the top Keller Williams real estate team. 40+ years of leadership, $1B+ closed in 2025, and the COLE Method that takes agents from producer to CEO.",
      },
      { property: "og:url", content: "https://joinkcn.com/" },
      { property: "og:type", content: "website" },
    ],
  }),
  component: Index,
});

const homeFaqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Who is Kristan Cole?",
      acceptedAnswer: {
        "@type": "Answer",
        text: `Kristan Cole is a real estate icon and expansion leader with 40+ years of industry leadership. Her national team closed more than $1 billion in volume across 3,000+ transactions in 2025 and operates across ${STATS_TEXT.markets} markets nationwide and growing.`,
      },
    },
    {
      "@type": "Question",
      name: "What is the Kristan Cole Network?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The Kristan Cole Network (KCN) is a national real estate expansion team that recruits and develops growing agents using the COLE Method™ - a proven framework for taking agents from chaos to clarity and from producer to CEO.",
      },
    },
    {
      "@type": "Question",
      name: "Where does the Kristan Cole Network operate?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The team has a strong presence in Arizona (Phoenix, Scottsdale, Mesa) and Alaska (Anchorage, Wasilla), with national expansion partners across the U.S.",
      },
    },
    {
      "@type": "Question",
      name: "What is the Monthly Market Round Table?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "It's an invite-only monthly virtual session where Kristan coaches her team on market shifts, scripts, systems, and leadership. Agents can request an invitation to attend.",
      },
    },
  ],
};

function Index() {
  return (
    <SiteLayout>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(homeFaqJsonLd) }}
      />


      {/* HERO + VISIBILITY AUDIT EXPLAINER - shared image background */}
      <section className="home-hero relative isolate overflow-hidden border-b border-border">
        <Picture
          src={heroAz}
          alt="Modern luxury desert home in Scottsdale, Arizona at golden hour"
          pictureClassName="home-hero-media absolute inset-0 block h-full w-full"
          className="h-full w-full object-cover"
          width={1920}
          height={1280}
          fetchPriority="high"
          decoding="async"
        />
        <div className="home-hero-content relative">
          {/* HERO */}
          <div className="container-edge min-h-[100svh] flex items-end pt-32 md:pt-40 lg:pt-44 pb-14 md:pb-16">
            <div className="w-full">
              <p className="eyebrow text-foreground/90 mb-8">
                Kristan Cole Network
              </p>
              <h1 className="headline-xl max-w-5xl">
                A path to wealth
                <br />
                <span className="serif-italic text-foreground/85">and freedom.</span>
              </h1>
              <p className="mt-8 max-w-2xl font-display italic text-2xl md:text-3xl text-foreground/90 leading-snug">
                Decide to do what others won't.
              </p>
              <p className="mt-6 max-w-xl text-lg text-foreground/90 leading-relaxed">
                A national real estate team for agents ready to lead like CEOs,
                building a business that funds the life they actually want.
              </p>
              <div className="mt-10">
                <CTAButton href={BRAND.links.visibilityAudit} size="lg" variant="outline">
                  Start Here · Free
                </CTAButton>
              </div>
            </div>
          </div>

          {/* Hairline divider over the image */}
          <div className="container-edge">
            <div className="h-px w-full bg-foreground/15" />
          </div>

          {/* VISIBILITY AUDIT EXPLAINER */}
          <div className="container-edge py-8 md:py-12">
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-8">
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.1] text-foreground">
                  Build what lasts.{" "}
                  <span className="serif-italic text-foreground/85">That's how you get free.</span>
                </h2>
                <p className="mt-6 max-w-2xl text-foreground/90 leading-relaxed text-lg">
                  Bought leads are rent. Zillow, paid ads, lead vendors - they
                  stop the moment you stop paying. Owned visibility is an asset:
                  ranking in Google, in the map, in the directories, and getting
                  cited by AI like ChatGPT. It compounds, builds equity, and
                  funds a business that funds your life instead of consuming it.
                  That's the model. We'll show you how to run it.
                </p>
                <p className="mt-4 max-w-2xl font-display italic text-lg text-foreground/90">
                  Start with a free audit. See how buyers, sellers, and AI find you today - and where you're still paying rent.
                </p>

              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <CTAButton href={BRAND.links.visibilityAudit} size="lg" variant="outline">
                  Get Your Free Audit
                </CTAButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <Section tone="dark" className="py-0 md:py-0 lg:py-0">
        <div className="pt-2 pb-2">
          <StatsBand />
        </div>
      </Section>

      {/* PAYOFFS BAND */}
      <Section tone="darker" className="pt-10 md:pt-14 lg:pt-14">
        <div className="mb-10 max-w-3xl">

          <SectionEyebrow>The payoffs</SectionEyebrow>
          <h2 className="headline-lg">
            What a path to wealth and freedom
            <br />
            <span className="serif-italic text-primary">actually buys you.</span>
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-border">
          {[
            { t: "Wealth", b: "Equity, not just income. A business that compounds." },
            { t: "Access", b: "Kristan, the leadership team, and the rooms that move careers." },
            { t: "Freedom", b: "Systems that run without you in every seat." },
            { t: "Time", b: "Evenings back. Weekends back. A calendar you own." },
            { t: "Recognition", b: "A national brand with proof - not a logo on your card." },
            { t: "Expansion", b: "A real path to your own market, your own team, your own legacy." },
          ].map((p) => (
            <div key={p.t} className="bg-background p-6 md:p-10">
              <h3 className="font-display text-3xl text-primary">{p.t}</h3>
              <p className="mt-4 text-foreground/85 leading-relaxed">{p.b}</p>
            </div>
          ))}
        </div>
      </Section>



      {/* PROMISE */}
      <Section tone="cream">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionEyebrow>The Promise</SectionEyebrow>
            <h2 className="headline-lg">
              A real estate business that
              <span className="serif-italic text-primary"> funds your life</span>
              <span className="text-cream-foreground/40"> - </span>
              instead of consuming it.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 space-y-6 text-cream-foreground/85 text-lg leading-relaxed">
            <p>
              Most agents hit a ceiling. They chase the next lead, then the next,
              and somewhere along the way the business they built starts running
              them. We've watched it for forty years.
            </p>
            <p>
              The Kristan Cole Network is built for the agent who's ready to lead
              like a CEO - to install the systems, the people, and the framework
              that make growth sustainable. Calm. Faith-anchored. Clear.
            </p>
            <p className="font-display italic text-2xl text-cream-foreground">
              "Clarity brings peace. Structure creates freedom."
            </p>
          </div>
        </div>
      </Section>

      {/* FOUR PILLARS - Grit, Grace, Growth, Give (shared) */}
      <ValuesSection tone="darker">


        <div className="mt-12">
          <Link
            to="/why-join-kcn"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-foreground hover:text-primary transition-colors"
          >
            Read all the reasons agents make the move
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </ValuesSection>

      {/* COLE METHOD preview */}
      <Section tone="dark">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <SectionEyebrow>The Framework</SectionEyebrow>
            <h2 className="headline-lg">
              The COLE Method<span className="text-primary">™</span>
            </h2>
            <p className="mt-6 text-foreground/90 text-lg leading-relaxed">
              Four pillars that take an agent from chaos to clarity - and from
              clarity to compounding wealth. It's how Kristan built a $1B+ team
              without losing her family, her faith, or her sanity.
            </p>
            <ul className="mt-10 divide-y divide-border border-y border-border">
              {[
                ["Clarity", "Define the business that fits your calling."],
                ["Operations", "Install the systems that remove you as the bottleneck."],
                ["Leverage", "Hire and lead the people who multiply your output."],
                ["Expansion", "Scale into a legacy business that pays you back."],
              ].map(([k, v]) => (
                <li key={k} className="flex flex-col md:flex-row gap-2 md:gap-8 py-5">
                  <span className="font-display text-2xl text-primary md:w-40">{k}</span>
                  <span className="text-foreground/90 flex-1">{v}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10">
              <CTAButton href="/the-cole-method" variant="outline">
                Explore the Method
              </CTAButton>
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <Picture
              src={agentsAz}
              alt="Real estate agents in an Arizona luxury community at sunset"
              className="w-full h-[60vh] object-cover"
              width={1600}
              height={1200}
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </Section>

      {/* PERSONAS */}
      <Section tone="cream">
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-7">
            <SectionEyebrow>Is this you?</SectionEyebrow>
            <h2 className="headline-lg">
              The agents who thrive here all share
              <span className="serif-italic text-primary"> one thing</span>.
            </h2>
            <p className="mt-6 text-cream-foreground/85 text-lg">
              They've outgrown the grind. They want a business - not just a
              pipeline. Whether you're producing, plateauing, or just starting,
              there's a seat at this table.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-px bg-cream-foreground/10">
          {[
            {
              who: "The purpose-driven producer",
              quote:
                "I'm closing deals, but I'm losing my evenings. I want to grow without giving up my family.",
              tag: "Legacy Builder",
            },
            {
              who: "The burned-out top agent",
              quote:
                "I'm the bottleneck. I know I need leverage - I just don't trust most teams to build it the right way.",
              tag: "Future CEO",
            },
            {
              who: "The values-aligned newcomer",
              quote:
                "I prayed for a place that wasn't all hustle. I need mentorship that lines up with what I actually believe.",
              tag: "Rising Pro",
            },
          ].map((p) => (
            <div key={p.who} className="bg-cream p-6 md:p-10">
              <p className="eyebrow text-primary mb-6">{p.tag}</p>
              <p className="font-display text-2xl leading-snug text-cream-foreground">
                "{p.quote}"
              </p>
              <p className="mt-8 text-sm uppercase tracking-[0.18em] text-cream-foreground/70">
               - {p.who}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-12">
          <Link
            to="/agents"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-cream-foreground hover:text-primary transition-colors"
          >
            See if this team fits your season
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Section>

      {/* ROUND TABLE feature - the main CTA, dressed up */}
      <section className="relative isolate overflow-hidden">
        {/* Top red accent bar */}
        <div className="h-1.5 w-full bg-primary" />

        <div className="relative">
          <Picture
            src={roundtableImg}
            alt="Real estate professionals around a conference table"
            pictureClassName="absolute inset-0 block h-full w-full"
            className="h-full w-full object-cover"
            width={1920}
            height={1280}
            loading="lazy"
            decoding="async"
          />
          {/* Lighter overlays so the image reads through */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />

          <div className="relative container-edge py-14 md:py-20 lg:py-24">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-3 mb-8 px-4 py-2 border border-primary/60 bg-primary/10 backdrop-blur-sm">
                <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                <p className="text-[0.7rem] uppercase tracking-[0.25em] text-foreground font-medium">
                  Invite-Only · By Approval
                </p>
              </div>


              <h2 className="font-display text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tight text-foreground">
                The Monthly Market
                <br />
                <span className="serif-italic text-primary">Round Table.</span>
              </h2>

              <p className="mt-8 text-xl text-foreground leading-relaxed max-w-xl">
                A seat in the room with Kristan's inner circle. The scripts,
                market shifts, and systems that built a{" "}
                <span className="font-semibold text-foreground underline decoration-primary decoration-2 underline-offset-4">$1B+ operation</span>{" "}
               - shared with the few agents we invite each month.
              </p>

              <p className="mt-6 max-w-xl font-display italic text-lg text-foreground/90">
                The agents who own their market are the ones who understand it.
                That's why every month starts with the data.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-foreground/85">
                <span className="flex items-center gap-2">
                  <span className="text-primary">◆</span> Inner-circle access
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-primary">◆</span> Approval required
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-primary">◆</span> Limited seats each month
                </span>
              </div>

              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <CTAButton href="/round-table" size="lg">
                  Explore the Round Table
                </CTAButton>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom red accent bar */}
        <div className="h-1.5 w-full bg-primary" />
      </section>

      {/* PILLAR POINTS STRIP */}
      <section className="bg-background border-y border-border">
        <div className="container-edge py-12 md:py-14 grid md:grid-cols-3 gap-10 md:gap-12 text-center">
          {[
            "Fund your life.",
            "Get passionate about boring.",
            "Follow a proven path of a proven leader.",
          ].map((line) => (
            <p
              key={line}
              className="font-display italic text-2xl md:text-3xl leading-snug text-foreground"
            >
              "{line}"
            </p>
          ))}
        </div>
      </section>

      {/* KRISTAN intro */}
      <Section tone="dark">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Picture
              src={BRAND.profilePics[0]}
              alt="Kristan Cole - founder of the Kristan Cole Network"
              className="w-full aspect-[4/5] object-cover"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <SectionEyebrow>About Kristan</SectionEyebrow>
            <h2 className="headline-lg">
              Real estate
              <br />
              <span className="serif-italic text-primary">icon.</span>
            </h2>
            <p className="mt-6 text-foreground/90 text-lg leading-relaxed">
              40 years in real estate. $1B+ closed in 2025 across 3,000+
              transactions. A national network of KW brokerages across {STATS_TEXT.markets}
              markets. Mentee of John C. Maxwell and Gary Keller. Realtor of
              the Year. 2018 Inman Innovator finalist. Founder of Kristan's
              Home of Hope.
            </p>
            <p className="mt-4 text-foreground/90 text-lg leading-relaxed">
              She doesn't sell hype. She hands agents a proven path.
            </p>
            <div className="mt-10">
              <CTAButton href="/kristan" variant="outline">
                Read Kristan's Story
              </CTAButton>
            </div>
          </div>
        </div>
      </Section>

      {/* VISION */}
      <Section tone="darker">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionEyebrow>Our Vision</SectionEyebrow>
            <h2 className="headline-lg">
              A drama-free organization of
              <br />
              <span className="serif-italic text-primary">highly productive agents.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-foreground/90 text-xl md:text-2xl leading-snug font-display">
              With actual wealth opportunities for those who want to be millionaires.
            </p>
            <p className="mt-6 text-foreground/85 leading-relaxed">
              If that vision is yours, you're in the right place. These four
              values are how we live it out.
            </p>
            <ul className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6">
              {["Grit", "Grace", "Growth", "Give"].map((v) => (
                <li key={v}>
                  <p className="font-display text-2xl md:text-3xl text-foreground">{v}</p>
                  <span className="mt-2 block h-px w-8 bg-primary" />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* REVIEWS */}
      <Section tone="darker" className="pt-12 md:pt-16">

        <div className="mb-12">
          <SectionEyebrow>From the team &amp; clients</SectionEyebrow>
          <h2 className="headline-lg max-w-3xl">
            What people who work with us actually say.
          </h2>
        </div>
        <ReviewsWidget />
      </Section>

      {/* NATIONWIDE FOOTPRINT */}
      <Section tone="dark">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-10">
          <div className="lg:col-span-7">
            <SectionEyebrow>Nationwide footprint</SectionEyebrow>
            <h2 className="headline-lg mb-6">
              {MARKETS_COUNT}+ markets.
              <br />
              <span className="serif-italic text-primary">One playbook.</span>
            </h2>
            <p className="text-foreground/80 text-lg leading-[1.7] max-w-2xl">
              From Alaska to Florida, Arizona to Wisconsin - KCN partners
              run the same systems in their own backyards. Hover the map to
              see who's already in.
            </p>
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <Link
              to="/kcn-worldwide"
              className="inline-flex items-center gap-3 text-sm uppercase tracking-[0.22em] text-primary border-b border-primary/40 hover:border-foreground hover:text-foreground transition-colors pb-1"
            >
              Explore all locations <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        <div className="w-full">
          <USMarketsMap
            markers={locations.map((l) => ({
              name: l.name,
              region: l.region,
              url: l.url,
              coordinates: l.coords,
            }))}
          />
        </div>
      </Section>



      <CTABanner cta="audit" />
    </SiteLayout>
  );
}
